import { useState } from "react";
import { api, MODELS, DISCIPLINES, GEOGRAPHIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plane, Search, Loader2, ExternalLink, Coins, Calendar, MapPin, Users, Printer, ChevronDown, X } from "lucide-react";

const COUNTRIES = [
  "Global",
  "India","United States","United Kingdom","Canada","Australia","Germany","France","Italy","Netherlands","Sweden","Switzerland","Ireland","Spain","Portugal",
  "Japan","South Korea","China","Singapore","Malaysia","Indonesia","Thailand","Vietnam","Philippines",
  "United Arab Emirates","Saudi Arabia","Qatar","Israel","Turkey",
  "South Africa","Kenya","Nigeria","Egypt",
  "Brazil","Mexico","Argentina","Chile","Colombia",
  "New Zealand",
];

const AGENCIES = [
  "Any",
  "SERB (India)","DBT (India)","ICMR (India)","DST (India)","CSIR (India)","INSA (India)","UGC (India)","AICTE (India)","MoES (India)","MeitY (India)",
  "NSF (USA)","NIH (USA)","DoE (USA)","DARPA (USA)","Fulbright","IIE (USA)",
  "IEEE","ACM","ASME","APS","OSA / Optica","ACS","RSC","Wiley","Springer Nature",
  "DAAD (Germany)","DFG (Germany)","BMBF (Germany)","VolkswagenStiftung",
  "CNRS (France)","ANR (France)","INRIA","Institut Pasteur",
  "UKRI / EPSRC / BBSRC / MRC","Royal Society","British Council","Newton Fund",
  "European Commission (Horizon Europe)","Marie Skłodowska-Curie Actions","ERC","Erasmus+",
  "JSPS (Japan)","JST (Japan)","MEXT (Japan)","KAKENHI",
  "NRF (Korea)","NRF (South Africa)","NSFC (China)","MOST (Taiwan)",
  "NHMRC (Australia)","ARC (Australia)","Endeavour",
  "IDRC (Canada)","NSERC (Canada)","CIHR (Canada)","SSHRC (Canada)",
  "World Bank","UNDP","UNESCO","UNICEF","WHO","ADB","AfDB",
  "Gates Foundation","Wellcome Trust","Templeton","Rockefeller","Ford Foundation",
];

export default function Travel() {
  const [q, setQ] = useState({ countries: ["Global"], discipline: "", topic: "", agency: "Any", modelIdx: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleCountry = (c) => setQ(prev => {
    const has = prev.countries.includes(c);
    return { ...prev, countries: has ? prev.countries.filter(x => x !== c) : [...prev.countries, c] };
  });

  const search = async () => {
    setLoading(true); setItems([]);
    const tid = toast.loading("Scouting travel grants — up to a minute…");
    try {
      const m = MODELS[q.modelIdx];
      const { data } = await api.post("/travel/search", {
        countries: q.countries,
        country: q.countries[0] || "Global", // back-compat
        discipline: q.discipline,
        topic: q.topic,
        agency: q.agency === "Any" ? "" : q.agency,
        model_provider: m.provider, model_name: m.name,
      });
      setItems(data.items || []);
      toast.dismiss(tid); toast.success(`Found ${data.items?.length || 0} grants`);
    } catch (e) { toast.dismiss(tid); toast.error(e?.response?.data?.detail || "Search failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="overline">Mobility</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><Plane className="w-10 h-10 text-primary"/>Travel Grants</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Discover travel &amp; conference-support grants worldwide — filter by countries, discipline, topic, or sponsoring agency and get recommendations for research meets and academic exchanges.</p>
        </div>
        {items.length>0 && <Button variant="outline" className="rounded-full" onClick={()=>window.print()}><Printer className="w-4 h-4 mr-2"/>Print</Button>}
      </div>

      <div className="card-crisp rounded-lg p-5 mb-6 grid md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-2">
          <Label>Countries <span className="text-muted-foreground text-xs">({q.countries.length})</span></Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-between" data-testid="travel-country-btn">
                <span className="truncate text-left">{q.countries.length ? q.countries.slice(0,3).join(", ") + (q.countries.length > 3 ? ` +${q.countries.length-3}` : "") : "Any"}</span>
                <ChevronDown className="w-4 h-4 opacity-60"/>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <div className="p-2 border-b border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{q.countries.length} selected</span>
                <button className="text-primary hover:underline" onClick={()=>setQ(p=>({...p, countries: []}))}>Clear</button>
              </div>
              <div className="max-h-72 overflow-auto p-2 space-y-1" data-testid="travel-country-list">
                {COUNTRIES.map(c => (
                  <label key={c} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-secondary cursor-pointer">
                    <Checkbox checked={q.countries.includes(c)} onCheckedChange={()=>toggleCountry(c)} data-testid={`travel-country-${c.replace(/[^A-Za-z0-9]/g,"_")}`}/>
                    <span className="text-sm">{c}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {q.countries.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {q.countries.map(c => (
                <span key={c} className="inline-flex items-center gap-1 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                  {c}
                  <button onClick={()=>toggleCountry(c)} className="hover:text-destructive"><X className="w-2.5 h-2.5"/></button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label>Discipline</Label>
          <Select value={q.discipline || "any"} onValueChange={(v)=>setQ({...q, discipline: v === "any" ? "" : v})}>
            <SelectTrigger data-testid="travel-discipline"><SelectValue placeholder="Any"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {DISCIPLINES.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Sponsoring agency</Label>
          <Select value={q.agency} onValueChange={(v)=>setQ({...q, agency: v})}>
            <SelectTrigger data-testid="travel-agency"><SelectValue/></SelectTrigger>
            <SelectContent className="max-h-72">{AGENCIES.map(a=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Topic</Label>
          <Input value={q.topic} onChange={(e)=>setQ({...q, topic:e.target.value})} placeholder="AI, climate…" data-testid="travel-topic"/>
        </div>
        <Button onClick={search} disabled={loading || q.countries.length === 0} className="rounded-full" data-testid="travel-search-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Searching…</> : <><Search className="w-4 h-4 mr-2"/>Search</>}
        </Button>
      </div>

      <div className="mb-4 text-xs text-muted-foreground">
        <Label className="text-xs">Model</Label>
        <Select value={String(q.modelIdx)} onValueChange={(v)=>setQ({...q, modelIdx:Number(v)})}>
          <SelectTrigger className="w-72" data-testid="travel-model"><SelectValue/></SelectTrigger>
          <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {items.length === 0 && !loading && (
        <div className="card-crisp rounded-lg p-10 text-center">
          <Plane className="w-10 h-10 text-primary mx-auto mb-3"/>
          <div className="font-display text-2xl">Where should your research travel next?</div>
          <div className="text-muted-foreground mt-1">Pick one or more countries, a discipline, and a sponsoring agency — the studio will surface the most relevant travel grants.</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(it => (
          <div key={it.id} className="card-crisp rounded-lg p-5 hover:border-primary/60" data-testid={`travel-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="overline">{it.sponsor}</div>
                <div className="font-display text-2xl mt-1 leading-tight">{it.name}</div>
              </div>
              {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline inline-flex items-center gap-1">Apply <ExternalLink className="w-3 h-3"/></a>}
            </div>
            <div className="mt-3 text-sm text-muted-foreground">{it.summary}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-primary"/>{it.country}</div>
              <div className="flex items-center gap-1"><Calendar className="w-3 h-3 text-primary"/>{it.deadline || "Rolling"}</div>
              <div className="flex items-center gap-1"><Coins className="w-3 h-3 text-primary"/>{it.amount}</div>
              <div className="flex items-center gap-1"><Users className="w-3 h-3 text-primary"/>{it.eligibility?.slice(0,50)}…</div>
            </div>
            {it.purpose && <div className="mt-3 text-xs italic border-l-2 border-primary/40 pl-2">Purpose: {it.purpose}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
